import { Host, h } from "@stencil/core";
import { analyzeLabelExistence, getElementInternalsLabel } from "../../../common/analysis/accessibility";
import { COLOR_PICKER_PARTS_DICTIONARY, KEY_CODES } from "../../../common/reserved-names";
import { SyncWithRAF } from "../../../common/sync-with-frames";
import { tokenMap } from "../../../common/utils";
import { getColorFormat } from "../utils/color-format";
import { fromStringToColorVariants } from "../utils/color-variants";
const DEFAULT_COLOR_FORMAT = "hex";
const FALLBACK_COLOR = "#000000";
export class ChColorField {
    constructor() {
        this.colorVariants = undefined;
        this.step = 1;
        this.disabled = false;
        this.readonly = false;
        this.accessibleName = "Color field";
        this.accessibleRoleDescription = "2D color field";
        this.value = FALLBACK_COLOR;
    }
    #colorPickedVariants;
    #colorFormat = DEFAULT_COLOR_FORMAT;
    #currentX = 0;
    #currentY = 0;
    #isDragging = false;
    #isUserAction = false;
    // Observers
    #dragRAF;
    #resizeObserver;
    #resizeRAF = new SyncWithRAF();
    // DOM refs
    #canvasRef;
    #markerRef;
    #keyEvents = {
        [KEY_CODES.ARROW_UP]: ev => {
            this.#currentY -= this.step;
            // The marker is crossing the top edge, don't let it go over the edge
            if (this.#currentY < 0) {
                this.#currentY = 0;
            }
            this.#drawMarker();
            this.#emitColorChange();
            ev.preventDefault();
        },
        [KEY_CODES.ARROW_DOWN]: ev => {
            this.#currentY += this.step;
            const canvasHeight = this.#canvasRef?.height || 0;
            // The marker is crossing the bottom edge, don't let it go over the edge
            if (this.#currentY >= canvasHeight) {
                this.#currentY = canvasHeight;
            }
            this.#drawMarker();
            this.#emitColorChange();
            ev.preventDefault();
        },
        [KEY_CODES.ARROW_LEFT]: ev => {
            this.#currentX -= this.step;
            // The marker is crossing the left edge, don't let it go over the edge
            if (this.#currentX < 0) {
                this.#currentX = 0;
            }
            this.#drawMarker();
            this.#emitColorChange();
            ev.preventDefault();
        },
        [KEY_CODES.ARROW_RIGHT]: ev => {
            this.#currentX += this.step;
            const canvasWidth = this.#canvasRef?.width || 0;
            // The marker is crossing the right edge, don't let it go over the edge
            if (this.#currentX >= canvasWidth) {
                this.#currentX = canvasWidth;
            }
            this.#drawMarker();
            this.#emitColorChange();
            ev.preventDefault();
        }
    };
    internals;
    el;
    valueChanged(newValue, oldValue) {
        this.internals.setFormValue(this.value);
        if (this.#isUserAction) {
            this.#isUserAction = false;
            return;
        }
        this.#handleExternalValueChange(newValue, oldValue);
    }
    /**
     * The `input` event is emitted when a change to the element's value is
     * committed by the user.
     *
     * It contains the new value (in all variants) of the color-field.
     */
    input;
    #applyValidColor = (value, colorVariants) => {
        this.colorVariants = colorVariants;
        const format = getColorFormat(value);
        if (format) {
            this.#colorFormat = format;
        }
        this.#updateMarkerPosition();
        this.#drawColorField();
    };
    #drawColorField = () => {
        const ctx = this.#canvasRef.getContext("2d", { willReadFrequently: true });
        if (!ctx || !this.colorVariants) {
            return;
        }
        const { width, height } = this.#canvasRef;
        // Get the hue from the current color (HSV model)
        const hsvValues = this.colorVariants.hsv;
        // Clear the canvas
        ctx.clearRect(0, 0, width, height);
        // Create horizontal gradient from white to pure hue (saturation gradient)
        const saturationGradient = ctx.createLinearGradient(0, 0, width, 0);
        saturationGradient.addColorStop(0, `hsl(${hsvValues.h}, 0%, 100%)`);
        saturationGradient.addColorStop(1, `hsl(${hsvValues.h}, 100%, 50%)`);
        ctx.fillStyle = saturationGradient;
        ctx.fillRect(0, 0, width, height);
        // Apply vertical gradient from transparent to black (value gradient)
        const valueGradient = ctx.createLinearGradient(0, 0, 0, height);
        valueGradient.addColorStop(0, "rgba(0, 0, 0, 0)");
        valueGradient.addColorStop(1, "rgba(0, 0, 0, 1)");
        ctx.globalCompositeOperation = "multiply";
        ctx.fillStyle = valueGradient;
        ctx.fillRect(0, 0, width, height);
        // Reset composite operation
        ctx.globalCompositeOperation = "source-over";
        this.#drawMarker();
    };
    #drawMarker = () => {
        if (this.#markerRef) {
            this.#markerRef.style.left = `${this.#currentX}px`;
            this.#markerRef.style.top = `${this.#currentY}px`;
        }
    };
    #emitColorChange = () => {
        this.#colorPickedVariants = this.#getColorFromCanvas();
        const newValue = this.#getColorInOriginalFormat(this.#colorPickedVariants);
        this.#isUserAction = true;
        this.value = newValue;
        this.internals.setFormValue(newValue);
        this.input.emit(this.#colorPickedVariants);
    };
    #getColorInOriginalFormat = (colorVariants) => colorVariants[this.#colorFormat ?? DEFAULT_COLOR_FORMAT];
    #getColorFromCanvas = () => {
        const ctx = this.#canvasRef.getContext("2d", { willReadFrequently: true });
        if (!ctx) {
            // Return fallback color variants
            return fromStringToColorVariants(FALLBACK_COLOR);
        }
        // Ensure coordinates are within valid pixel range for getImageData
        const x = Math.min(this.#currentX, this.#canvasRef.width - 1);
        const y = Math.min(this.#currentY, this.#canvasRef.height - 1);
        const imageData = ctx.getImageData(x, y, 1, 1);
        const [r, g, b, a] = imageData.data;
        return fromStringToColorVariants(`rgba(${r}, ${g}, ${b}, ${a / 255})`);
    };
    #handleCanvasClick = (event) => {
        if (!this.#isDragging) {
            this.#updateCoordinates(event);
            this.#emitColorChange();
        }
    };
    #handleExternalValueChange = (newValue, oldValue) => {
        if (!newValue) {
            this.#revertToOldValue(oldValue);
            return;
        }
        const colorVariants = fromStringToColorVariants(newValue);
        if (!colorVariants) {
            this.#revertToOldValue(oldValue);
            return;
        }
        this.#applyValidColor(newValue, colorVariants);
    };
    #handleMouseDown = (event) => {
        event.preventDefault();
        this.#dragRAF ??= new SyncWithRAF();
        this.#updateCoordinates(event);
        // Add listeners to document for better drag experience
        document.addEventListener("mousemove", this.#handleMouseMove, {
            capture: true
        });
        document.addEventListener("mouseup", this.#handleMouseUp, {
            capture: true,
            passive: true
        });
    };
    #handleMouseMove = (event) => {
        this.#isDragging ||= true;
        this.#dragRAF.perform(() => {
            this.#updateCoordinates(event);
        }, () => {
            // Store coordinates immediately for responsiveness with proper clamping
            const rect = this.#canvasRef.getBoundingClientRect();
            const canvasHeight = this.#canvasRef.height;
            const canvasWidth = this.#canvasRef.width;
            this.#currentX = Math.max(0, Math.min(canvasWidth, event.clientX - rect.left));
            this.#currentY = Math.max(0, Math.min(canvasHeight, event.clientY - rect.top));
        });
    };
    #handleMouseUp = () => {
        if (this.#isDragging) {
            this.#isDragging = false;
            this.#emitColorChange();
        }
        // Remove listeners
        document.removeEventListener("mousemove", this.#handleMouseMove, {
            capture: true
        });
        document.removeEventListener("mouseup", this.#handleMouseUp, {
            capture: true
        });
        // Free up memory by setting dragRAF to undefined
        this.#dragRAF?.cancel();
        this.#dragRAF = undefined;
    };
    #handleKeyDown = (event) => {
        const keyEventHandler = this.#keyEvents[event.code];
        if (!keyEventHandler) {
            return;
        }
        keyEventHandler(event);
    };
    #updateCanvasSize = () => {
        if (this.#canvasRef) {
            this.#canvasRef.width = this.#canvasRef.clientWidth;
            this.#canvasRef.height = this.#canvasRef.clientHeight;
            this.#drawColorField();
        }
    };
    #revertToOldValue = (oldValue) => {
        const fallbackValue = oldValue || FALLBACK_COLOR;
        this.#isUserAction = true;
        this.value = fallbackValue;
    };
    #updateMarkerPosition = () => {
        if (!this.colorVariants || !this.#canvasRef) {
            return;
        }
        // Use HSV for marker positioning
        const hsvValues = this.colorVariants.hsv;
        // X represents saturation (0-100%)
        this.#currentX = (hsvValues.s / 100) * this.#canvasRef.width;
        // Y represents value (0% value = bottom, 100% value = top)
        this.#currentY = ((100 - hsvValues.v) / 100) * this.#canvasRef.height;
        this.#drawMarker();
    };
    #updateCoordinates = (event) => {
        const rect = this.#canvasRef.getBoundingClientRect();
        const canvasHeight = this.#canvasRef.height;
        const canvasWidth = this.#canvasRef.width;
        // Ensure valid pixel range
        this.#currentX = Math.max(0, Math.min(canvasWidth, event.clientX - rect.left));
        this.#currentY = Math.max(0, Math.min(canvasHeight, event.clientY - rect.top));
        this.#colorPickedVariants = this.#getColorFromCanvas();
        this.#drawMarker();
    };
    componentWillLoad() {
        // Initialize colorVariants before component loads to avoid state change on load
        if (this.value) {
            try {
                this.colorVariants = fromStringToColorVariants(this.value);
                const format = getColorFormat(this.value);
                if (format) {
                    this.#colorFormat = format;
                }
            }
            catch (error) {
                console.warn(`Invalid value color: ${this.value}`, error);
                this.colorVariants = fromStringToColorVariants(FALLBACK_COLOR);
                this.#colorFormat = DEFAULT_COLOR_FORMAT;
            }
        }
    }
    connectedCallback() {
        // Accessibility
        this.internals.setFormValue(this.value);
        const labels = this.internals.labels;
        const accessibleNameFromExternalLabel = getElementInternalsLabel(labels);
        // Report any accessibility issue
        analyzeLabelExistence(this.el, "ch-color-field", labels, accessibleNameFromExternalLabel, this.accessibleName);
    }
    componentDidLoad() {
        this.#updateCanvasSize();
        this.#updateMarkerPosition();
        // Initialize ResizeObserver with RAF optimization
        this.#resizeObserver = new ResizeObserver(() => {
            this.#resizeRAF.perform(() => {
                this.#updateCanvasSize();
                this.#updateMarkerPosition();
            });
        });
        // Observe the canvas for size changes
        this.#resizeObserver.observe(this.#canvasRef);
    }
    disconnectedCallback() {
        // Disconnect the observer when the component is unloaded and clean up the reference
        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
            this.#resizeObserver = undefined;
        }
        // Cancel pending RAF operations
        this.#resizeRAF.cancel();
        this.#dragRAF?.cancel();
        // Clean up RAF references
        this.#resizeRAF = undefined;
        this.#dragRAF = undefined;
        // Clean up event listeners
        document.removeEventListener("mousemove", this.#handleMouseMove, {
            capture: true
        });
        document.removeEventListener("mouseup", this.#handleMouseUp, {
            capture: true
        });
    }
    render() {
        const isInteractive = !this.disabled && !this.readonly;
        return (h(Host, { key: 'b2350e1b702fa33cdcfd9f182e251eee34933c67', role: "application", "aria-disabled": this.disabled ? "true" : null, "aria-label": this.accessibleName, "aria-readonly": this.readonly ? "true" : null, "aria-roledescription": this.accessibleRoleDescription, onKeyDown: isInteractive ? this.#handleKeyDown : null, tabindex: !this.disabled ? "0" : null }, h("canvas", { key: '85881a111d777e296f012b14f235649dea06eb8f', "aria-hidden": "true", onClick: isInteractive ? this.#handleCanvasClick : null, onMouseDown: isInteractive ? this.#handleMouseDown : null, ref: el => (this.#canvasRef = el) }), h("div", { key: '8eb1b4052c5b79fc80fa7172eeb6595bd4263bfb', class: "marker", part: tokenMap({
                [COLOR_PICKER_PARTS_DICTIONARY.MARKER]: true,
                [COLOR_PICKER_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [COLOR_PICKER_PARTS_DICTIONARY.READONLY]: this.readonly
            }), ref: el => {
                this.#markerRef = el;
            } })));
    }
    static get is() { return "ch-color-field"; }
    static get encapsulation() { return "shadow"; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["color-field.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["color-field.css"]
        };
    }
    static get properties() {
        return {
            "step": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Step size in pixels for keyboard navigation on the canvas.\nDetermines how many pixels the marker moves when using arrow keys.\nDefault = 1."
                },
                "attribute": "step",
                "reflect": false,
                "defaultValue": "1"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "readonly": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute indicates that the user cannot modify the value of the control.\nSame as [readonly](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-readonly)\nattribute for `input` elements."
                },
                "attribute": "readonly",
                "reflect": false,
                "defaultValue": "false"
            },
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false,
                "defaultValue": "\"Color field\""
            },
            "accessibleRoleDescription": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a readable description for the component\u2019s role, primarily\nused by assistive technologies to give users more context about the\ncomponent's purpose or behavior."
                },
                "attribute": "accessible-role-description",
                "reflect": false,
                "defaultValue": "\"2D color field\""
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "example",
                            "text": "// HEX format\nvalue = \"#FF00AA\""
                        }, {
                            "name": "example",
                            "text": "// HSL format\nvalue = \"hsl(120, 100%, 25%)\""
                        }, {
                            "name": "example",
                            "text": "// RGB format\nvalue = \"rgb(255, 125, 50)\""
                        }],
                    "text": "The current value of the `ch-color-field` component, representing a color\nin one of the following formats:\n  - HEX\n  - HSL\n  - RGB\nThis value determines the selected color and can be updated by the user."
                },
                "attribute": "value",
                "reflect": false,
                "defaultValue": "FALLBACK_COLOR"
            }
        };
    }
    static get states() {
        return {
            "colorVariants": {}
        };
    }
    static get events() {
        return [{
                "method": "input",
                "name": "input",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "The `input` event is emitted when a change to the element's value is\ncommitted by the user.\n\nIt contains the new value (in all variants) of the color-field."
                },
                "complexType": {
                    "original": "ColorVariants",
                    "resolved": "{ rgb: string; rgba: string; hsl: string; hsla: string; hex: string; hsv: { h: number; s: number; v: number; }; }",
                    "references": {
                        "ColorVariants": {
                            "location": "import",
                            "path": "../../../common/types",
                            "id": "src/common/types.ts::ColorVariants"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "value",
                "methodName": "valueChanged"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
